import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type { ChecklistQuery, ChecklistResponse, ChecklistStatus } from '@troia-v3/types';

import {
  Button,
  FiltersButton,
  ListPageEmpty,
  ListPageLoading,
  ListPagePagination,
  PageToolbar,
  PeriodPicker,
  PlainHeadCell,
  Select,
  SortableHeadCell,
  StatusPill,
  ToolbarSearch,
  ViewToggle,
} from '@/shared/components/ui';
import type { PeriodPickerValue, StatusPillVariant } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import { useUnitsDropdown } from '@/shared/hooks/useUnitsDropdown';
import { useUsersDropdown } from '@/modules/users/hooks/useUsers';

import { ApplyChecklistTemplateModal } from '../components/ApplyChecklistTemplateModal';
import { useChecklists } from '../hooks/useChecklists';

type ViewMode = 'grid' | 'table';
type SortKey = 'template' | 'status' | 'due' | 'score';

/**
 * Metadados visuais de `ChecklistStatus` (spec: pending cinza, in_progress
 * azul, completed âmbar "Aguardando revisão", approved verde, rejected
 * vermelho, expired vermelho-escuro).
 *
 * `StatusPill` shared (F62-F65) só expõe 5 variants semânticas
 * (success/warning/error/info/neutral) — não há um "vermelho-escuro"
 * dedicado. `rejected` e `expired` dividem `variant: 'error'` e se
 * diferenciam pelo RÓTULO ("Reprovado" vs "Expirado"); criar uma cor nova
 * fora do primitivo violaria a regra de padronização de UI (CLAUDE.md
 * frontend, "Todo papel de UI tem UM primitivo shared dono").
 */
export const CHECKLIST_STATUS_META: Record<ChecklistStatus, { label: string; variant: StatusPillVariant; rank: number }> = {
  pending: { label: 'Pendente', variant: 'neutral', rank: 0 },
  in_progress: { label: 'Em andamento', variant: 'info', rank: 1 },
  completed: { label: 'Aguardando revisão', variant: 'warning', rank: 2 },
  approved: { label: 'Aprovado', variant: 'success', rank: 3 },
  rejected: { label: 'Reprovado', variant: 'error', rank: 4 },
  expired: { label: 'Expirado', variant: 'error', rank: 5 },
};

const STATUS_OPTIONS: Array<{ value: ChecklistStatus | undefined; label: string }> = [
  { value: undefined, label: 'Todos' },
  { value: 'pending', label: 'Pendente' },
  { value: 'in_progress', label: 'Em andamento' },
  { value: 'completed', label: 'Aguardando revisão' },
  { value: 'approved', label: 'Aprovado' },
  { value: 'rejected', label: 'Reprovado' },
  { value: 'expired', label: 'Expirado' },
];

/** Janela rolling (dias) por modo do PeriodPicker — molde `CRMReportsPage`. */
const PERIOD_MODE_DAYS: Record<'week' | 'month' | 'year', number> = { week: 7, month: 30, year: 365 };

const isoDate = (date: Date): string => date.toISOString().split('T')[0]!;

const periodToRange = (period: PeriodPickerValue): { dateFrom: string; dateTo: string } => {
  if (period.mode === 'custom' && period.start && period.end) {
    return { dateFrom: period.start, dateTo: period.end };
  }
  const days = period.mode === 'custom' ? PERIOD_MODE_DAYS.month : PERIOD_MODE_DAYS[period.mode];
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - days);
  return { dateFrom: isoDate(start), dateTo: isoDate(end) };
};

const formatDayMonth = (iso: string): string => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;

/**
 * "prazo relativo" — `formatDistanceToNow` já resolve passado ("há 2h") e
 * futuro ("em 3h"). Exportado: `ChecklistReviewPage` reusa o mesmo texto
 * quando o checklist ainda não foi enviado (`completedAt` ausente).
 */
export const formatDueRelative = (expiresAt?: string): { label: string; overdue: boolean } => {
  if (!expiresAt) return { label: 'Sem prazo', overdue: false };
  const due = new Date(expiresAt);
  if (Number.isNaN(due.getTime())) return { label: 'Sem prazo', overdue: false };
  const overdue = due.getTime() < Date.now();
  return { label: formatDistanceToNow(due, { addSuffix: true, locale: ptBR }), overdue };
};

/**
 * A listagem de gestão mostra só aplicações de CHECKLIST. Formulário também é
 * aplicação (`checklists` com `templateType: 'form'` — captura de lead, aba do
 * drawer, workflow), mas não pertence a esta tela. Filtro fixo, fora da contagem
 * de filtros ativos e preservado ao limpar.
 */
const BASE_FILTERS: NonNullable<ChecklistQuery['filters']> = { templateType: 'checklist' };

const formatScore = (score?: number): string => (score === undefined ? '—' : `${Math.round(score * 100)}%`);

/**
 * ChecklistsPage — listagem de gestão de instâncias de checklist.
 *
 * `assigneeName`/`unitName` NÃO são populados pelo backend
 * (`ChecklistsRepository.mapToResponse` — comentário explícito: "campos
 * Two-Phase Fetch... NÃO são preenchidos aqui, enriquecimento é
 * responsabilidade do service" — e `ChecklistsService` nunca os preenche).
 * A listagem resolve os dois client-side via os hooks dropdown existentes
 * (`useUsersDropdown`/`useUnitsDropdown`, molde da Task 13/14). Já o
 * "modelo" NÃO precisa de resolução — `templateSnapshot.name` é uma cópia
 * imutável do nome gravada na criação (mais confiável que um lookup ao vivo,
 * que quebraria se o modelo fosse renomeado ou excluído depois).
 */
export const ChecklistsPage: React.FC = () => {
  const navigate = useNavigate();

  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [searchTerm, setSearchTerm] = useState('');
  const [query, setQuery] = useState<ChecklistQuery>({ page: 1, limit: 20, sortBy: 'due', sortOrder: 'asc', filters: BASE_FILTERS });
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'due', dir: 'asc' });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [periodValue, setPeriodValue] = useState<PeriodPickerValue | null>(null);
  // "Novo checklist" — cria uma instância AVULSA (sem `linkedEntity`), o caminho
  // que faltava: até então só dava pra criar um checklist a partir do drawer de
  // um lead/ticket, e a listagem de gestão só tinha CTA pra criar MODELO.
  const [newChecklistOpen, setNewChecklistOpen] = useState(false);

  const { data, isLoading, error } = useChecklists(query);
  const { data: unitOptions } = useUnitsDropdown();
  const { data: userOptions } = useUsersDropdown();

  const unitMap = useMemo(() => new Map((unitOptions ?? []).map((u) => [u.value, u.label])), [unitOptions]);
  const userMap = useMemo(() => new Map((userOptions ?? []).map((u) => [u.id, u.name])), [userOptions]);

  const items = useMemo(() => data?.items ?? [], [data]);

  // A ordenação acontece no SERVIDOR (`sortBy`/`sortOrder` na query). Ordenar
  // client-side reordenava só os 20 itens da página em cima de um `createdAt
  // desc` — a coluna "Prazo" prometia urgência e entregava recência, deixando
  // o checklist mais atrasado do tenant escondido na página 3.
  const sortedItems = items;

  const setSearch = (term: string) => {
    setSearchTerm(term);
    setQuery((prev) => ({ ...prev, search: term || undefined, page: 1 }));
  };

  const setStatusFilter = (status?: ChecklistStatus) => {
    setQuery((prev) => ({ ...prev, page: 1, filters: { ...prev.filters, status } }));
  };

  const setUnitFilter = (unitId: string) => {
    setQuery((prev) => ({ ...prev, page: 1, filters: { ...prev.filters, unitId: unitId || undefined } }));
  };

  const setAssigneeFilter = (assigneeId: string) => {
    setQuery((prev) => ({ ...prev, page: 1, filters: { ...prev.filters, assigneeId: assigneeId || undefined } }));
  };

  const applyPeriod = (value: PeriodPickerValue) => {
    setPeriodValue(value);
    const { dateFrom, dateTo } = periodToRange(value);
    setQuery((prev) => ({ ...prev, page: 1, filters: { ...prev.filters, dateFrom, dateTo } }));
  };

  const clearPeriod = () => {
    setPeriodValue(null);
    setQuery((prev) => ({ ...prev, page: 1, filters: { ...prev.filters, dateFrom: undefined, dateTo: undefined } }));
  };

  const clearAllFilters = () => {
    setPeriodValue(null);
    setQuery((prev) => ({ ...prev, page: 1, filters: BASE_FILTERS }));
    setFiltersOpen(false);
  };

  const handleSort = (key: SortKey) => {
    const dir: 'asc' | 'desc' = sort.key === key && sort.dir === 'asc' ? 'desc' : 'asc';
    setSort({ key, dir });
    // Página 1: com ordenação global, manter a página atual mostraria uma
    // fatia do meio da nova ordem.
    setQuery((prev) => ({ ...prev, sortBy: key, sortOrder: dir, page: 1 }));
  };

  const statusLabel = STATUS_OPTIONS.find((o) => o.value === query.filters?.status)?.label ?? null;
  const unitLabel = query.filters?.unitId ? (unitMap.get(query.filters.unitId) ?? null) : null;
  const assigneeLabel = query.filters?.assigneeId ? (userMap.get(query.filters.assigneeId) ?? null) : null;
  const periodLabel = periodValue
    ? periodValue.mode === 'custom' && periodValue.start && periodValue.end
      ? `${formatDayMonth(periodValue.start)} - ${formatDayMonth(periodValue.end)}`
      : periodValue.mode === 'week'
        ? 'Última semana'
        : periodValue.mode === 'year'
          ? 'Último ano'
          : 'Último mês'
    : null;

  const activeFilterCount = [
    query.filters?.status !== undefined,
    !!query.filters?.unitId,
    !!query.filters?.assigneeId,
    !!periodValue,
  ].filter(Boolean).length;

  const filterOptions = (
    <div className="space-y-3">
      <div>
        <p className="px-0.5 pb-1 text-[11px] font-bold uppercase tracking-wider" style={{ color: '#94a3b8' }}>Status</p>
        <div className="space-y-0.5">
          {STATUS_OPTIONS.map((opt) => {
            const active = (query.filters?.status ?? undefined) === opt.value;
            return (
              <button
                key={opt.label}
                onClick={() => setStatusFilter(opt.value)}
                className="w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-sm font-medium text-left"
                style={{ color: '#374151', backgroundColor: active ? '#f3e8ff' : 'transparent' }}
                onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
                onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; }}
                data-testid={`checklists-filter-status-${opt.value ?? 'all'}`}
              >
                {opt.label}
                {active && <Icon name="check" className="w-4 h-4" style={{ color: '#8b5cf6' }} />}
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <p className="px-0.5 pb-1 text-[11px] font-bold uppercase tracking-wider" style={{ color: '#94a3b8' }}>Unidade</p>
        <Select
          value={query.filters?.unitId ?? ''}
          onChange={setUnitFilter}
          options={unitOptions ?? []}
          placeholder="Todas as unidades"
          data-testid="checklists-filter-unit"
        />
      </div>
      <div>
        <p className="px-0.5 pb-1 text-[11px] font-bold uppercase tracking-wider" style={{ color: '#94a3b8' }}>Responsável</p>
        <Select
          value={query.filters?.assigneeId ?? ''}
          onChange={setAssigneeFilter}
          options={(userOptions ?? []).map((u) => ({ value: u.id, label: u.name }))}
          placeholder="Todos os responsáveis"
          data-testid="checklists-filter-assignee"
        />
      </div>
      <div>
        <div className="flex items-center justify-between px-0.5 pb-1">
          <p className="text-[11px] font-bold uppercase tracking-wider" style={{ color: '#94a3b8' }}>Período</p>
          {periodValue && (
            <button onClick={clearPeriod} className="text-xs font-semibold" style={{ color: '#8b5cf6' }} data-testid="checklists-filter-period-clear">
              Limpar
            </button>
          )}
        </div>
        <PeriodPicker value={periodValue ?? { mode: 'month' }} onChange={applyPeriod} data-testid="checklists-filter-period" />
      </div>
    </div>
  );

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--theme-page-background)' }}>
      <PageToolbar
        left={
          <>
            <nav className="flex items-center gap-2 min-w-0">
              <span className="font-semibold text-base truncate" style={{ color: 'var(--theme-text)' }}>Checklists</span>
            </nav>
            {data?.total != null && (
              <span
                className="text-xs font-semibold rounded-full px-2 py-0.5 tabular-nums flex-none"
                style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-muted-text)' }}
                data-testid="checklists-count"
              >
                {data.total}
              </span>
            )}
          </>
        }
        center={
          <ToolbarSearch
            value={searchTerm}
            onChange={setSearch}
            placeholder="Buscar checklist…"
            data-testid="checklists-search"
          />
        }
        right={
          <>
            <div className="relative flex-none">
              <FiltersButton
                count={activeFilterCount}
                active={filtersOpen}
                onClick={() => setFiltersOpen((v) => !v)}
                data-testid="checklists-filters"
              />
              {filtersOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setFiltersOpen(false)} />
                  <div
                    className="absolute right-0 top-full mt-2 z-50 w-72 rounded-xl p-2 max-h-[75vh] overflow-y-auto"
                    style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', boxShadow: '0 4px 24px rgba(0,0,0,.12)' }}
                    data-testid="checklists-filters-popover"
                  >
                    {filterOptions}
                  </div>
                </>
              )}
            </div>

            <ViewToggle
              modes={[
                { id: 'table', icon: 'list', label: 'Lista' },
                { id: 'grid', icon: 'grid', label: 'Cards' },
              ]}
              value={viewMode}
              onChange={setViewMode}
              data-testid="checklists-view"
            />

            {/* .tb-sep antes do CTA (ordem canônica: Filtros → Toggle → sep → CTA) */}
            <span className="hidden md:block w-px h-[22px] flex-none" style={{ backgroundColor: 'var(--theme-input-border)' }} />

            <Button
              variant="primary"
              icon="plus"
              collapsibleLabel
              onClick={() => setNewChecklistOpen(true)}
              data-testid="new-checklist-btn"
            >
              Novo checklist
            </Button>
          </>
        }
        mobile={
          <>
            <ToolbarSearch
              value={searchTerm}
              onChange={setSearch}
              placeholder="Buscar checklist…"
              data-testid="checklists-search-mobile"
            />
            <button
              onClick={() => setFiltersOpen((v) => !v)}
              className="inline-flex items-center gap-1.5 h-[38px] px-3 rounded-[10px] text-[13px] font-semibold whitespace-nowrap flex-none"
              style={{ border: '1px solid var(--theme-input-border)', backgroundColor: 'var(--theme-card-background)', color: 'var(--theme-card-text)' }}
              data-testid="checklists-filters-mobile"
            >
              <Icon name="filter" className="w-4 h-4" style={{ color: 'var(--theme-muted-text)' }} />
              Filtros
              {activeFilterCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold text-white" style={{ backgroundColor: 'var(--theme-primary)' }}>{activeFilterCount}</span>
              )}
            </button>
          </>
        }
      />

      {activeFilterCount > 0 && (
        <div className="flex-none flex items-center flex-wrap gap-2 px-4 py-2" style={{ backgroundColor: 'var(--theme-header-background)', borderBottom: '1px solid var(--theme-header-border)' }}>
          {statusLabel && (
            <span className="inline-flex items-center gap-1 h-[26px] pl-2.5 pr-1 rounded-full text-xs font-semibold" style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}>
              Status: {statusLabel}
              <button onClick={() => setStatusFilter(undefined)} className="w-[18px] h-[18px] rounded-full inline-flex items-center justify-center opacity-60 hover:opacity-100" aria-label="Remover filtro de status" data-testid="checklists-chip-status-remove">
                <Icon name="x" className="w-3 h-3" />
              </button>
            </span>
          )}
          {unitLabel && (
            <span className="inline-flex items-center gap-1 h-[26px] pl-2.5 pr-1 rounded-full text-xs font-semibold" style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}>
              Unidade: {unitLabel}
              <button onClick={() => setUnitFilter('')} className="w-[18px] h-[18px] rounded-full inline-flex items-center justify-center opacity-60 hover:opacity-100" aria-label="Remover filtro de unidade" data-testid="checklists-chip-unit-remove">
                <Icon name="x" className="w-3 h-3" />
              </button>
            </span>
          )}
          {assigneeLabel && (
            <span className="inline-flex items-center gap-1 h-[26px] pl-2.5 pr-1 rounded-full text-xs font-semibold" style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}>
              Responsável: {assigneeLabel}
              <button onClick={() => setAssigneeFilter('')} className="w-[18px] h-[18px] rounded-full inline-flex items-center justify-center opacity-60 hover:opacity-100" aria-label="Remover filtro de responsável" data-testid="checklists-chip-assignee-remove">
                <Icon name="x" className="w-3 h-3" />
              </button>
            </span>
          )}
          {periodLabel && (
            <span className="inline-flex items-center gap-1 h-[26px] pl-2.5 pr-1 rounded-full text-xs font-semibold" style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}>
              Período: {periodLabel}
              <button onClick={clearPeriod} className="w-[18px] h-[18px] rounded-full inline-flex items-center justify-center opacity-60 hover:opacity-100" aria-label="Remover filtro de período" data-testid="checklists-chip-period-remove">
                <Icon name="x" className="w-3 h-3" />
              </button>
            </span>
          )}
          <button onClick={clearAllFilters} className="text-xs font-semibold" style={{ color: 'var(--theme-muted-text)' }} data-testid="checklists-clear-filters">Limpar tudo</button>
        </div>
      )}

      <div className="flex-1 overflow-auto">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-6">
          {error && (
            <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--theme-error-subtle)', border: '1px solid var(--theme-error-color)' }} data-testid="checklists-error">
              <p style={{ color: 'var(--theme-error-fg)' }}>Erro ao carregar checklists: {error.message}</p>
            </div>
          )}

          {isLoading && <ListPageLoading message="Carregando checklists..." />}

          {!isLoading && !error && items.length === 0 && (
            <ListPageEmpty
              icon="check-square"
              message="Nenhum checklist encontrado"
              description={searchTerm || activeFilterCount > 0 ? 'Tente ajustar a busca ou os filtros.' : 'Crie um checklist a partir de um modelo para atribuí-lo a um responsável.'}
              {...(searchTerm || activeFilterCount > 0
                ? {}
                : { actionLabel: 'Novo checklist', onAction: () => setNewChecklistOpen(true) })}
            />
          )}

          {!isLoading && !error && items.length > 0 && viewMode === 'grid' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="checklists-grid">
              {sortedItems.map((checklist) => (
                <ChecklistCard
                  key={checklist.id}
                  checklist={checklist}
                  unitName={checklist.unitId ? unitMap.get(checklist.unitId) : undefined}
                  assigneeName={userMap.get(checklist.assigneeId)}
                  onClick={() => navigate(`/checklists/${checklist.id}`)}
                />
              ))}
            </div>
          )}

          {!isLoading && !error && items.length > 0 && viewMode === 'table' && (
            <>
              <div className="grid grid-cols-1 gap-4 md:hidden">
                {sortedItems.map((checklist) => (
                  <ChecklistCard
                    key={checklist.id}
                    checklist={checklist}
                    unitName={checklist.unitId ? unitMap.get(checklist.unitId) : undefined}
                    assigneeName={userMap.get(checklist.assigneeId)}
                    onClick={() => navigate(`/checklists/${checklist.id}`)}
                  />
                ))}
              </div>
              <div className="hidden md:block rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }}>
                <table className="w-full border-collapse" data-testid="checklists-table">
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--theme-card-border)' }}>
                      <SortableHeadCell label="Modelo" sortKey="template" activeKey={sort.key} dir={sort.dir} onSort={handleSort} />
                      <PlainHeadCell label="Unidade" />
                      <PlainHeadCell label="Responsável" />
                      <SortableHeadCell label="Status" sortKey="status" activeKey={sort.key} dir={sort.dir} onSort={handleSort} />
                      <SortableHeadCell label="Prazo" sortKey="due" activeKey={sort.key} dir={sort.dir} onSort={handleSort} />
                      <SortableHeadCell label="Score" sortKey="score" activeKey={sort.key} dir={sort.dir} onSort={handleSort} />
                    </tr>
                  </thead>
                  <tbody>
                    {sortedItems.map((checklist) => (
                      <ChecklistRow
                        key={checklist.id}
                        checklist={checklist}
                        unitName={checklist.unitId ? unitMap.get(checklist.unitId) : undefined}
                        assigneeName={userMap.get(checklist.assigneeId)}
                        onClick={() => navigate(`/checklists/${checklist.id}`)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {!isLoading && !error && data?.page != null && data?.totalPages != null && data.total > 0 && (
            <ListPagePagination
              currentPage={data.page}
              totalPages={data.totalPages}
              onPageChange={(page) => setQuery((prev) => ({ ...prev, page }))}
              totalItems={data.total}
              pageSize={data.limit}
              itemLabel="checklists"
            />
          )}
        </div>
      </div>

      <ApplyChecklistTemplateModal
        isOpen={newChecklistOpen}
        onClose={() => setNewChecklistOpen(false)}
        showUnitField
        lockedType="checklist"
        title="Novo checklist"
        confirmLabel="Criar checklist"
        testIdPrefix="new-checklist"
        onCreated={(checklist) => navigate(`/checklists/${checklist.id}`)}
      />
    </div>
  );
};

ChecklistsPage.displayName = 'ChecklistsPage';

/* ------------------------------------------------------------------ */
/* Linha de tabela / card                                              */
/* ------------------------------------------------------------------ */

interface ChecklistRowProps {
  checklist: ChecklistResponse;
  unitName?: string;
  assigneeName?: string;
  onClick: () => void;
}

const ChecklistRow: React.FC<ChecklistRowProps> = ({ checklist, unitName, assigneeName, onClick }) => {
  const meta = CHECKLIST_STATUS_META[checklist.status];
  const due = formatDueRelative(checklist.expiresAt);
  const isPending = checklist.status === 'pending' || checklist.status === 'in_progress';

  return (
    <tr
      className="cursor-pointer transition-colors border-t"
      style={{ borderColor: 'var(--theme-card-border)' }}
      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)')}
      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
      onClick={onClick}
      data-testid={`checklist-row-${checklist.id}`}
    >
      <td className="px-4 py-3 text-sm font-semibold truncate max-w-[280px]" style={{ color: 'var(--theme-card-text)' }}>
        {checklist.templateSnapshot.name}
      </td>
      <td className="px-4 py-3 text-sm" style={{ color: 'var(--theme-card-text)' }}>{unitName ?? '—'}</td>
      <td className="px-4 py-3 text-sm" style={{ color: 'var(--theme-card-text)' }}>{assigneeName ?? '—'}</td>
      <td className="px-4 py-3">
        <StatusPill variant={meta.variant} label={meta.label} data-testid={`checklist-status-${checklist.id}`} />
      </td>
      <td className="px-4 py-3 text-sm" style={{ color: isPending && due.overdue ? 'var(--theme-error-fg)' : 'var(--theme-muted-text)' }}>
        {due.label}
      </td>
      <td className="px-4 py-3 text-sm tabular-nums" style={{ color: 'var(--theme-card-text)' }}>{formatScore(checklist.score)}</td>
    </tr>
  );
};

const ChecklistCard: React.FC<ChecklistRowProps> = ({ checklist, unitName, assigneeName, onClick }) => {
  const meta = CHECKLIST_STATUS_META[checklist.status];
  const due = formatDueRelative(checklist.expiresAt);
  const isPending = checklist.status === 'pending' || checklist.status === 'in_progress';

  return (
    <div
      className="rounded-xl p-4 cursor-pointer transition-colors"
      style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }}
      onClick={onClick}
      data-testid={`checklist-card-${checklist.id}`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <h3 className="text-sm font-semibold truncate" style={{ color: 'var(--theme-card-text)' }}>
          {checklist.templateSnapshot.name}
        </h3>
        <StatusPill variant={meta.variant} label={meta.label} />
      </div>
      <p className="text-xs mb-1" style={{ color: 'var(--theme-muted-text)' }}>
        {unitName ? `${unitName} · ` : ''}{assigneeName ?? 'Sem responsável'}
      </p>
      <div className="flex items-center justify-between mt-3">
        <span className="text-xs" style={{ color: isPending && due.overdue ? 'var(--theme-error-fg)' : 'var(--theme-muted-text)' }}>
          {due.label}
        </span>
        <span className="text-xs font-semibold tabular-nums" style={{ color: 'var(--theme-card-text)' }}>
          {formatScore(checklist.score)}
        </span>
      </div>
    </div>
  );
};
